import { WORKSPACE_TYPE, type WorkspaceType } from './constants';

/**
 * Qué módulos existen en cada tipo de espacio.
 *
 * El fallo que motivó este catálogo: quien tiene una cuenta personal y otra de
 * empresa cambiaba de una a otra y el menú apenas se movía —nueve de las doce
 * secciones eran idénticas—, así que el selector de espacio no comunicaba
 * nada. Peor: «Revisiones» y «Actividad» seguían ahí en el espacio personal,
 * la primera sin sentido fuera de un encargo profesional y la segunda
 * devolviendo un 403 porque `audit_logs` está desactivado en los cuatro planes
 * personales. Dos puertas que no llevaban a ningún sitio.
 *
 * Antes la regla vivía repartida en tres lugares que no se hablaban entre sí:
 * dos listas de menú escritas a mano en el frontend, un prefijo de ruta
 * (`/business/`) para decidir de dónde expulsar al cambiar de espacio, y nada
 * en la API. Bastaba escribir la URL para entrar igual. Aquí está una sola
 * vez, en el paquete que comparten frontend y API, para que no puedan
 * contradecirse.
 *
 * **No es una comprobación de permisos.** Quién puede hacer qué dentro de un
 * espacio lo decide `@29100/permissions`; esto decide qué existe en el
 * espacio, que es una pregunta anterior.
 */
export const WORKSPACE_MODULE = {
  DASHBOARD: 'DASHBOARD',
  FILES: 'FILES',
  PHOTOS: 'PHOTOS',
  ALBUMS: 'ALBUMS',
  FAVORITES: 'FAVORITES',
  CHECKLISTS: 'CHECKLISTS',
  REVIEWS: 'REVIEWS',
  SHARED_LINKS: 'SHARED_LINKS',
  CLEANUP: 'CLEANUP',
  TRASH: 'TRASH',
  ACTIVITY: 'ACTIVITY',
  SETTINGS: 'SETTINGS',
  SHARED_FOLDERS: 'SHARED_FOLDERS',
  TEAM: 'TEAM',
  DOCUMENT_REQUESTS: 'DOCUMENT_REQUESTS',
  REPORTS: 'REPORTS',
} as const;
export type WorkspaceModule = (typeof WORKSPACE_MODULE)[keyof typeof WORKSPACE_MODULE];

/**
 * A qué producto pertenece un módulo.
 *
 * `PERSONAL` y `BUSINESS` no son simétricos, y es a propósito:
 *
 * - `BUSINESS` significa **que no existe** en un espacio personal. La API lo
 *   rechaza y el interfaz ni lo ofrece ni deja entrar escribiendo la URL.
 * - `PERSONAL` significa que el menú de empresa no lo ofrece, pero la pantalla
 *   sigue funcionando dentro de una organización. Fotos, álbumes y favoritos
 *   son archivos como cualquier otro: echar de una página que funciona sería
 *   quitar algo que hoy se usa.
 */
export const MODULE_SCOPE = {
  BOTH: 'BOTH',
  PERSONAL: 'PERSONAL',
  BUSINESS: 'BUSINESS',
} as const;
export type ModuleScope = (typeof MODULE_SCOPE)[keyof typeof MODULE_SCOPE];

export const WORKSPACE_MODULE_SCOPE: Record<WorkspaceModule, ModuleScope> = {
  DASHBOARD: MODULE_SCOPE.BOTH,
  FILES: MODULE_SCOPE.BOTH,
  SHARED_LINKS: MODULE_SCOPE.BOTH,
  CLEANUP: MODULE_SCOPE.BOTH,
  TRASH: MODULE_SCOPE.BOTH,
  SETTINGS: MODULE_SCOPE.BOTH,

  /*
   * Las listas de verificación se quedan en ambos: el catálogo trae plantillas
   * para «Familia», «Estudiante» y «Uso personal» —el pasaporte, la matrícula,
   * la documentación del coche—, y retirarlas del espacio personal dejaría ese
   * trabajo del catálogo sin ninguna puerta por la que entrar.
   */
  CHECKLISTS: MODULE_SCOPE.BOTH,

  PHOTOS: MODULE_SCOPE.PERSONAL,
  ALBUMS: MODULE_SCOPE.PERSONAL,
  FAVORITES: MODULE_SCOPE.PERSONAL,

  /*
   * Las fechas de revisión son de empresa. Vencimiento de pólizas, renovación
   * de contratos, caducidad de certificados y plazos de conservación de un
   * encargo son plazos profesionales: dentro de un espacio personal la sección
   * ocupaba un sitio en el menú sin responder a ninguna pregunta que quien lo
   * usa se esté haciendo.
   */
  REVIEWS: MODULE_SCOPE.BUSINESS,

  /*
   * El registro de actividad es de empresa por dos motivos que apuntan al
   * mismo sitio. El primero es que ya lo era de hecho: `audit_logs` está en
   * `FALSE` en los cuatro planes personales, así que la sección abría y
   * respondía «no tienes permiso». El segundo es para qué sirve —saber quién
   * de tu equipo tocó qué—, que en un espacio de una sola persona no es una
   * pregunta.
   */
  ACTIVITY: MODULE_SCOPE.BUSINESS,

  SHARED_FOLDERS: MODULE_SCOPE.BUSINESS,
  TEAM: MODULE_SCOPE.BUSINESS,
  DOCUMENT_REQUESTS: MODULE_SCOPE.BUSINESS,
  REPORTS: MODULE_SCOPE.BUSINESS,
};

/**
 * ¿Existe este módulo en un espacio de este tipo?
 *
 * Es la pregunta que hacen la API antes de atender y el interfaz antes de
 * dejar entrar. Un módulo `PERSONAL` responde que sí en ambos: no se ofrece en
 * el menú de empresa, pero funciona.
 */
export function isModuleAvailable(module: WorkspaceModule, workspaceType: WorkspaceType): boolean {
  if (WORKSPACE_MODULE_SCOPE[module] !== MODULE_SCOPE.BUSINESS) return true;
  return workspaceType === WORKSPACE_TYPE.ORGANIZATION;
}

/** Módulos que el menú del espacio debe ofrecer, en el orden del catálogo. */
export function modulesForWorkspace(workspaceType: WorkspaceType): WorkspaceModule[] {
  const propio =
    workspaceType === WORKSPACE_TYPE.ORGANIZATION ? MODULE_SCOPE.BUSINESS : MODULE_SCOPE.PERSONAL;

  return (Object.keys(WORKSPACE_MODULE) as WorkspaceModule[]).filter((module) => {
    const alcance = WORKSPACE_MODULE_SCOPE[module];
    return alcance === MODULE_SCOPE.BOTH || alcance === propio;
  });
}
